import { debounce, fetchConfig } from '@theme/utilities';

/**
 * Order information block on the cart page.
 *
 * Persists every field as a Shopify cart attribute, so the whole order form
 * arrives attached to the order in admin with no app and no reconciliation.
 *
 * Shopify does not validate cart attributes — an empty required field will sail
 * through to checkout and produce an order with no delivery date. So this also
 * gates the checkout button.
 */

const SAME_AS_ORDER = 'Same as order information';
const UNSET = 'Select Shipping Address';

/** Order field -> shipping field, for the "same as order information" copy. */
const MIRROR = {
  'Order contact name': 'Shipping name',
  'Order contact number': 'Shipping contact',
  'Order address': 'Shipping address',
  'Order postcode': 'Shipping postcode',
  'Order city': 'Shipping city',
  'Order state': 'Shipping state',
  'Order country': 'Shipping country',
};

class YcOrderInformation extends HTMLElement {
  connectedCallback() {
    this.shippingFields = this.querySelector('[ref="shippingFields"]');
    this.status = this.querySelector('[ref="status"]');
    this.choice = this.querySelector('[name="attributes[Shipping address choice]"]');

    this.#bindPersistence();
    this.#bindShippingToggle();
    this.#bindCheckoutGate();
  }

  get fields() {
    return [...this.querySelectorAll('[name^="attributes["]')];
  }

  static keyOf(el) {
    return el.name.slice('attributes['.length, -1);
  }

  #bindPersistence() {
    const save = debounce(() => this.#saveAll(), 400);
    this.fields.forEach((el) => {
      el.addEventListener('input', () => {
        this.#clearFieldError(el);
        save();
      });
      el.addEventListener('change', () => {
        this.#clearFieldError(el);
        save();
      });
    });
  }

  /**
   * Send the whole set each time rather than one key at a time. Cart updates are
   * not ordered, so concurrent single-key PATCHes can land out of sequence and
   * resurrect a value the customer just cleared.
   */
  async #saveAll() {
    const attributes = {};
    for (const el of this.fields) attributes[YcOrderInformation.keyOf(el)] = el.value ?? '';

    this.#abort?.abort();
    const controller = new AbortController();
    this.#abort = controller;

    try {
      const config = fetchConfig('json', { body: JSON.stringify({ attributes }) });
      const response = await fetch(Theme.routes.cart_update_url, {
        ...config,
        signal: controller.signal,
      });
      if (!response.ok) throw new Error(String(response.status));
      this.#setStatus('Saved.');
    } catch (error) {
      if (error.name === 'AbortError') return;
      this.#setStatus('Could not save your details. Check your connection.', true);
    } finally {
      this.#abort = null;
    }
  }

  #abort = null;

  /**
   * The artwork keeps the shipping block visible at all times, so choosing
   * "same as order information" fills it in rather than hiding it. The customer
   * can still see and correct what will be shipped against.
   */
  #bindShippingToggle() {
    this.choice?.addEventListener('change', () => {
      if (this.choice.value === SAME_AS_ORDER) {
        this.#mirrorOrderIntoShipping();
      } else if (this.choice.value !== UNSET) {
        // Shipping elsewhere: clear the copied address so the customer types a
        // fresh one rather than editing someone else's details in place.
        this.#clearMirroredShipping();
      }
    });
  }

  #clearMirroredShipping() {
    for (const to of Object.values(MIRROR)) {
      const field = this.querySelector(`[name="attributes[${to}]"]`);
      if (field) field.value = '';
    }
    this.#saveAll();
  }

  /**
   * Copy the order address across so the saved attributes stay complete. Leaving
   * them blank would put an order in admin whose shipping block reads empty.
   */
  #mirrorOrderIntoShipping() {
    for (const [from, to] of Object.entries(MIRROR)) {
      const src = this.querySelector(`[name="attributes[${from}]"]`);
      const dst = this.querySelector(`[name="attributes[${to}]"]`);
      if (src && dst) dst.value = src.value;
    }
    this.#saveAll();
  }

  #bindCheckoutGate() {
    if (this.querySelector('.yc-order-info')?.dataset.gateCheckout === 'false') return;
    document.addEventListener(
      'click',
      (event) => {
        const button = event.target.closest?.('[name="checkout"], [href$="/checkout"]');
        if (!button) return;
          // Tier bands are variants, so a customer can pick one that does not
        // match what is actually in the cart. Liquid works the totals out at
        // render time and leaves the verdict on the summary element.
        const summary = document.querySelector('[data-tier-mismatch="true"]');
        if (summary) {
          event.preventDefault();
          event.stopImmediatePropagation();
          summary.scrollIntoView({ behavior: 'smooth', block: 'center' });
          return;
        }

        const missing = this.#missingFields();
        if (!missing.length) return;
        event.preventDefault();
        event.stopImmediatePropagation();
        this.#flagMissing(missing);
      },
      true
    );
  }

  /** Required, visible, and not left on the placeholder option. */
  #missingFields() {
    return this.fields.filter((el) => {
      if (el.dataset.ycRequired !== 'true') return false;
      if (el.closest('[hidden]')) return false;
      const value = el.value?.trim();
      return !value || value === UNSET;
    });
  }

  #flagMissing(missing) {
    missing.forEach((el) => {
      el.setAttribute('aria-invalid', 'true');
      const error = el.parentElement?.querySelector('.yc-field__error');
      if (error) error.hidden = false;
    });
    this.#setStatus(
      `${missing.length} required field${missing.length === 1 ? '' : 's'} still to fill in before checkout.`,
      true
    );
    missing[0]?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    missing[0]?.focus({ preventScroll: true });
  }

  #clearFieldError(el) {
    el.removeAttribute('aria-invalid');
    const error = el.parentElement?.querySelector('.yc-field__error');
    if (error) error.hidden = true;
  }

  #setStatus(message, isError = false) {
    if (!this.status) return;
    this.status.textContent = message;
    this.status.classList.toggle('yc-order-info__status--error', isError);
  }
}

if (!customElements.get('yc-order-information-component')) {
  customElements.define('yc-order-information-component', YcOrderInformation);
}
